/**
 * Helpers for the Entelligence CLI loopback login flow.
 *
 * The CLI starts a short-lived HTTP server on a loopback address and opens the
 * browser at `/login?cli=true&cli_redirect=<loopback>&cli_state=<nonce>`. After
 * the user authenticates we mint (or fetch) their API key and redirect the
 * browser back to that loopback URL with the key, so the CLI can capture it
 * without any copy-paste.
 *
 * Both helpers exist to keep the loopback contract (allowed hosts, query shape)
 * in one place — the login page and the `/cli-auth` page must agree on it.
 */

/**
 * Whether `raw` is a redirect we're willing to send an API key to.
 *
 * Only plain-HTTP loopback addresses qualify. This is the security gate that
 * stops the CLI flow from being turned into an open redirect that exfiltrates a
 * user's API key to an arbitrary origin — never relax it to allow remote hosts
 * or `https` external URLs.
 *
 * @param raw - Candidate redirect URL from the `cli_redirect` query param.
 * @returns True only for `http://localhost|127.0.0.1|[::1]` (any port/path).
 */
export function isLoopbackUrl(raw: string | null | undefined): boolean {
  if (!raw) {
    return false;
  }
  try {
    const url = new URL(raw);
    if (url.protocol !== 'http:') {
      return false;
    }
    // For IPv6, URL.hostname keeps the brackets (e.g. `http://[::1]:9/` ->
    // hostname `[::1]`), so the bracketed form is the one that actually matches.
    return (
      url.hostname === 'localhost' ||
      url.hostname === '127.0.0.1' ||
      url.hostname === '[::1]'
    );
  } catch {
    return false;
  }
}

/**
 * Build the final loopback URL the browser is redirected to, carrying the API
 * key and the CSRF state nonce back to the waiting CLI server.
 *
 * @param redirect - A loopback URL already validated with {@link isLoopbackUrl}.
 * @param apiKey - The user's Entelligence API key.
 * @param state - The opaque nonce the CLI generated; echoed back for CSRF checks.
 * @returns `redirect` with `api_key` and `state` query params appended.
 */
export function buildCliRedirect(
  redirect: string,
  apiKey: string,
  state: string
): string {
  const separator = redirect.includes('?') ? '&' : '?';
  return `${redirect}${separator}api_key=${encodeURIComponent(
    apiKey
  )}&state=${encodeURIComponent(state)}`;
}

/**
 * The CLI loopback handoff stashed by the login page, if one is pending and
 * safe to honor.
 *
 * The login page persists `cli_redirect` / `cli_state` to localStorage before
 * the OAuth round-trip. When the user is fully onboarded, the login page's own
 * redirect consumes them — but a first-time user is routed into onboarding
 * BEFORE that redirect runs, so the onboarding CLI step is the only place left
 * that can complete the handoff. Without it, `entelligence auth login` sits
 * waiting until timeout for exactly the users most likely to be logging in for
 * the first time.
 *
 * Applies the same gates as /cli-auth: the target must be a loopback URL and
 * the CSRF nonce must be present (the CLI's local server rejects a missing or
 * mismatched state, so firing without one would fail silently).
 *
 * @param storage - Storage to read from; defaults to `window.localStorage`.
 *   Injectable for tests.
 * @param now - Current time in ms; injectable for tests.
 * @returns The stashed `{ redirect, state }` pair, or `null` when nothing is
 *   pending, the stash expired, or it fails a gate. Never throws.
 */
export function pendingCliLoopback(
  storage?: CliStashStorage,
  now: number = Date.now()
): { redirect: string; state: string } | null {
  const { redirect, state } = readCliStash(storage, now);
  if (!redirect || !isLoopbackUrl(redirect) || !state) {
    return null;
  }
  return { redirect, state };
}

const CLI_STASH_KEYS = [
  'cli_signup_flow',
  'cli_redirect',
  'cli_state',
  'cli_stash_at',
] as const;

// A CLI login abandoned longer ago than this is stale: honoring it would push
// a later, unrelated sign-in on this browser into the CLI flow.
const CLI_STASH_MAX_AGE_MS = 60 * 60 * 1000;

type CliStashStorage = Pick<Storage, 'getItem' | 'removeItem'>;

export interface CliStash {
  signupFlow: boolean;
  redirect: string | null;
  state: string | null;
}

const EMPTY_CLI_STASH: CliStash = {
  signupFlow: false,
  redirect: null,
  state: null,
};

function localStore(): Storage | null {
  return typeof window !== 'undefined' ? window.localStorage : null;
}

/**
 * Remove every CLI stash key.
 * @param storage - Defaults to `window.localStorage`; injectable for tests.
 */
export function clearCliStash(storage?: Pick<Storage, 'removeItem'>): void {
  try {
    const store = storage ?? localStore();
    CLI_STASH_KEYS.forEach((key) => store?.removeItem(key));
  } catch {
    // storage can throw in privacy modes; nothing to clear
  }
}

/**
 * The CLI login stash, or an empty one when absent or expired.
 *
 * A stash older than an hour (or with an unreadable `cli_stash_at`) is cleared
 * and treated as absent. A stash with no `cli_stash_at` was written before the
 * timestamp existed; it stays valid so a CLI login in flight across the deploy
 * still finishes.
 * @param storage - Defaults to `window.localStorage`; injectable for tests.
 * @param now - Current time in ms; injectable for tests.
 * @returns The stash. Never throws.
 */
export function readCliStash(
  storage?: CliStashStorage,
  now: number = Date.now()
): CliStash {
  try {
    const store = storage ?? localStore();
    if (!store) return EMPTY_CLI_STASH;
    const stashedAt = store.getItem('cli_stash_at');
    if (stashedAt !== null) {
      const at = Number(stashedAt);
      if (!Number.isFinite(at) || now - at > CLI_STASH_MAX_AGE_MS) {
        clearCliStash(store);
        return EMPTY_CLI_STASH;
      }
    }
    return {
      signupFlow: store.getItem('cli_signup_flow') === 'true',
      redirect: store.getItem('cli_redirect'),
      state: store.getItem('cli_state'),
    };
  } catch {
    return EMPTY_CLI_STASH; // storage access can throw in privacy modes
  }
}

/**
 * Stash the CLI login flow so onboarding and CLIApiKeySetup can resume it
 * after OAuth. Keys match what the login page and onboarding read.
 * @param p - The `cli_redirect` / `cli_state` values from the login URL.
 * @param storage - Defaults to `window.localStorage`; injectable for tests.
 * @param now - Stash time in ms, read by `readCliStash`; injectable for tests.
 */
export function stashCliFlow(
  p: { cliRedirect: string | null; cliState: string | null },
  storage?: Pick<Storage, 'setItem'>,
  now: number = Date.now()
): void {
  try {
    const store = storage ?? window.localStorage;
    store.setItem('cli_signup_flow', 'true');
    store.setItem('cli_stash_at', String(now));
    if (p.cliRedirect) store.setItem('cli_redirect', p.cliRedirect);
    if (p.cliState) store.setItem('cli_state', p.cliState);
  } catch {
    // storage can throw in privacy modes; callers must still navigate
  }
}
